import { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Spinner } from '../../components/ui';
import { adminAPI } from '../../api';

const STATUSES = [
  { k: '',                 l: 'All statuses' },
  { k: 'in_progress',      l: 'In progress' },
  { k: 'obs_complete',     l: 'Observed' },
  { k: 'debrief_pending',  l: 'Debrief pending' },
  { k: 'debrief_complete', l: 'Debriefed' },
  { k: 'fully_complete',   l: 'Sent' },
];

/** Every observation the account is entitled to see, across all schools. */
export default function ObservationsTab() {
  const [rows, setRows]       = useState([]);
  const [meta, setMeta]       = useState({ count: 0, flagged: 0 });
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ school_id: '', status: '', from: '', to: '' });

  const load = useCallback(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    Promise.all([adminAPI.observations(params), adminAPI.schools()])
      .then(([o, s]) => {
        setRows(o.data.results);
        setMeta({ count: o.data.count, flagged: o.data.flagged });
        setSchools(s.data);
      })
      .catch(err => { console.error(err); toast.error('Could not load observations.'); })
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(load, [load]);

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  return (
    <>
      <div className="adm-toolbar">
        <select className="fi" value={filters.school_id} onChange={e => set('school_id', e.target.value)}>
          <option value="">All schools</option>
          {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select className="fi" value={filters.status} onChange={e => set('status', e.target.value)}>
          {STATUSES.map(s => <option key={s.k} value={s.k}>{s.l}</option>)}
        </select>
      </div>
      <div className="adm-toolbar">
        <input className="fi" type="date" value={filters.from}
               onChange={e => set('from', e.target.value)} title="From" />
        <input className="fi" type="date" value={filters.to}
               onChange={e => set('to', e.target.value)} title="To" />
      </div>

      <div className="adm-note">
        <strong>{meta.count}</strong> observation{meta.count === 1 ? '' : 's'} match
        {meta.flagged > 0 && <> · <strong>{meta.flagged}</strong> flagged for thin or skewed evidence</>}
        {rows.length < meta.count && <> · showing the first {rows.length}</>}
      </div>

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'32px 0' }}><Spinner /></div>
      ) : rows.length === 0 ? (
        <div className="dash-empty">No observations match those filters.</div>
      ) : rows.map(o => (
        <div key={o.id} className="adm-user">
          <div className="adm-user-main">
            <div className="adm-user-name">
              {o.teacher_display_name || o.teacher_name || 'Unknown teacher'}
            </div>
            <div className="adm-user-sub">
              {[o.school_name, o.grade_level, o.subject].filter(Boolean).join(' · ')}
            </div>
            <div className="adm-user-meta">
              {o.observation_date} · {o.status_display} · {o.note_count} note{o.note_count === 1 ? '' : 's'}
              {o.lead_observer_name && ` · observed by ${o.lead_observer_name}`}
            </div>
          </div>
        </div>
      ))}
    </>
  );
}
